import { useEffect, useState } from 'react';
import styles from './ConnectionAlertBar.module.scss';
import { animated, useSpring } from '@react-spring/web';
import { useTranslation } from 'react-i18next';
import { logger } from '@shared/logger';
import usePreviewerStore from '@core/stores/usePreviewerStore';

/**
 * ConnectionAlertBar component displays a notification bar that alerts the user
 * about slow network conditions. It is automatically shown when the network is slow
 * and animated to slide down from the top of the screen.
 *
 * @returns The rendered connection alert bar, or null if the connection is not slow.
 */
const ConnectionAlertBar = () => {
    const [isSlow, setIsSlow] = useState(false);
    const { t } = useTranslation();
    const isPreviewerOpen = usePreviewerStore((state) => state.isPreviewerOpen);
    const speedtestThreshold = Number(import.meta.env.VITE_SPEEDTEST_THRESHOLD ?? 1000);
    const speedtestInterval = Number(import.meta.env.VITE_SPEEDTEST_INTERVAL ?? 10000);

    /**
     * testResponseTime makes a HEAD request to measure network response time.
     */
    const testResponseTime = async () => {
        const startTime = performance.now();
        try {
            await fetch(import.meta.env.VITE_SPEEDTEST_URL, {
                method: 'HEAD',
                mode: 'no-cors',
            });
            const endTime = performance.now();
            window.responseTime = Math.round(endTime - startTime);
            logger.log(
                'ConnectionAlertBar',
                3,
                'Response time test completed:',
                window.responseTime,
            );
        } catch (error) {
            logger.warn('ConnectionAlertBar', 1, 'Response has an error');
        }
    };

    /**
     * Determines if the connection is slow based on the latest response time.
     */
    const connectionIsSlow = () => {
        // return connectionIsSlow based on the response time threshold, or if !isPreviewerOpen return false
        return window.responseTime > speedtestThreshold;
    };

    useEffect(() => {
        // Single interval to run network tests and update the UI.
        const intervalDuration = speedtestInterval;

        const interval = setInterval(async () => {
            // Only run tests when the document is visible.
            if (document.visibilityState === 'visible') {
                await testResponseTime();
                setIsSlow(connectionIsSlow());
            }
        }, intervalDuration);

        // Additionally, when the tab becomes active, perform an immediate check.
        const handleVisibilityChange = async () => {
            if (document.visibilityState === 'visible') {
                await testResponseTime();
                setIsSlow(connectionIsSlow());
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    const animation = useSpring({
        transform: isSlow && !isPreviewerOpen ? 'translateY(0%)' : 'translateY(-100%)',
        config: { tension: 200, friction: 20 },
    });

    return (
        <animated.div style={animation} className={styles.alertBar}>
            {isSlow && !isPreviewerOpen && t('slowNetwork')}
        </animated.div>
    );
};

export default ConnectionAlertBar;
